<script setup lang="ts">
import RlUploadMultiple from '@/components/common/upload/RlUploadMultiple.vue'
import RepairSection from '@/components/rc/miner-repairs/repairs-sidebar/RepairSection.vue'
import { useMinerDetails } from '@/composables/rc/miner-details'
import useMinerStressTests from '@/composables/rc/miner-stress-tests'
import { DiagnosticResult, type IStressTest } from '@/types/rc/miners'
import type { UploadFile } from 'element-plus'
import { computed, ref, toRefs } from 'vue'
import AttachmentsList from './AttachmentsList.vue'
import { Icon } from '@iconify/vue'

const props = defineProps<{ minerId: number }>()
const emit = defineEmits(['showAll'])

const { minerId } = toRefs(props)

const { minerDetailsQuery } = useMinerDetails(minerId)
const { data: minerDetails } = minerDetailsQuery
const { createStressTestMutation } = useMinerStressTests(minerId)

const latestStressTest = computed(() =>
  minerDetails.value && minerDetails.value.condition.stress_tested && minerDetails.value.condition.last_stress_test
    ? minerDetails.value.condition.last_stress_test
    : null
)

// Form we'll post
const newStressTest = ref<Partial<IStressTest>>({})

// UI flags
const addingStressTest = ref(false)

// Additional files
const additionalFiles = ref<UploadFile[]>([])

const cancelAddStressTest = () => {
  addingStressTest.value = false
  newStressTest.value = {}
  additionalFiles.value = []
}

const { mutate: createStressTest, isPending: stressTestMutationPending } = createStressTestMutation

const saveStressTest = () => {
  if (!newStressTest.value.result || additionalFiles.value.length === 0) {
    return
  }
  createStressTest(
    {
      stress_test: {
        date: new Date(),
        result: newStressTest.value.result,
        failure_reason: newStressTest.value.failure_reasons,
        files: additionalFiles.value,
      },
    },
    {
      onSuccess: () => {
        additionalFiles.value = []
        newStressTest.value = {}
      },
      onSettled: () => {
        addingStressTest.value = false
      },
    }
  )
}

const showAllStressTests = () => {
  emit('showAll')
}
</script>

<template>
  <!-- No stress tests, display just "Add stress test" button -->
  <RepairSection
    v-if="!latestStressTest && !addingStressTest"
    class="stress-section__empty"
    title="Stress tests"
    :step="4"
    :active="false"
  >
    <ElText class="my-1 text-text-brand cursor-pointer self-start" @click="addingStressTest = true"
      >+ Add stress test</ElText
    >
  </RepairSection>

  <!-- If we're adding a stress test, display uploader -->
  <RepairSection v-if="addingStressTest" title="Stress tests" :step="4" :active="false">
    <el-radio-group v-model="newStressTest.result">
      <el-radio value="Pass" size="large">Passed</el-radio>
      <el-radio value="Fail" size="large">Failed</el-radio>
    </el-radio-group>

    <RlUploadMultiple v-model="additionalFiles" :limit="5" style="width: 100%" />

    <el-input
      v-model="newStressTest.failure_reasons"
      style="width: 100%"
      :rows="2"
      type="textarea"
      placeholder="Enter Stress Test Notes..."
    />

    <div
      style="display: flex; align-items: center; justify-content: flex-end; gap: var(--spacing-spacing-sm); width: 100%"
    >
      <el-button @click="cancelAddStressTest">Cancel</el-button>
      <el-button
        type="primary"
        :disabled="
          !newStressTest.result ||
          additionalFiles.length === 0 ||
          (newStressTest.result === DiagnosticResult.FAIL && !newStressTest.failure_reasons)
        "
        :style="{ margin: '0.7em 0' }"
        :loading="stressTestMutationPending"
        @click="saveStressTest"
      >
        Save
      </el-button>
    </div>
  </RepairSection>

  <!-- If a stress test came from the server and we're not adding one, display the server one -->
  <RepairSection
    v-if="!addingStressTest && latestStressTest"
    title="Stress tests"
    :step="4"
    :active="latestStressTest.result == DiagnosticResult.PASS"
  >
    <div class="w-full flex flex-col gap-2">
      <!-- Enable "Show all" button -->
      <div class="w-full flex items-center justify-between">
        <p>Latest stress test</p>
        <p class="text-button" @click="showAllStressTests"><span>Show all</span></p>
      </div>

      <!-- Failed/Passed label -->
      <div :class="[latestStressTest.result?.toLowerCase(), 'flex flex-row items-center gap-2']">
        <template v-if="latestStressTest.result === 'Fail'"
          ><p>Failed</p>
          <Icon icon="mdi:cancel-bold"
        /></template>
        <template v-else
          ><p>Passed</p>
          <Icon icon="mdi:check"
        /></template>
      </div>

      <div class="w-full">
        <AttachmentsList :files="latestStressTest.files" />
      </div>

      <!-- Notes -->
      <el-input v-model="latestStressTest.failure_reasons" disabled style="width: 100%" :rows="2" type="textarea" />

      <!-- Add another -->
      <p class="text-button" @click="addingStressTest = true"><span>+ Add stress test</span></p>
    </div>
  </RepairSection>
</template>

<style lang="scss" scoped>
.stress-section__empty {
  min-height: 3em;
}

.pass {
  color: var(--component-stroke-emerald);
}

.fail {
  color: var(--component-stroke-error);
}

.text-button {
  margin: 0.3em 0;

  span {
    color: var(--text-brand);
    cursor: pointer;
  }
}
</style>
